import React, { useState, useCallback } from "react";
import styles from "../customerInfo/CustomerInfo.module.scss";
import sharedStyles from "../OrderDetailsNewVersion.module.scss";
import Grid from "@mui/material/Grid";
import { withTranslation } from "react-i18next";
import crStyles from "../../../customerReservation/crStyles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UpperCaseText from "../../../../utils/UpperCaseText";
import {
  StyledBasicExpansionPanel,
  StyledBasicExpansionPanelSummary,
} from "../../../../shared/ExpansionPanels/ExpansionPanel";
import HasPermissionTo from "../../../../common/HasPermissionTo";
import { BEV_VEHICLES, TRANSIT_VEHICLES } from "../../../../common/Constants";

const InfoDetail = ({ header, value }) => (
  <>
    <div className={styles.itemHeaderText}>{header}</div>
    <div className={styles.itemValueText}>{value || "--"}</div>
  </>
);

function OrderInfo({ customerReservation, permissions, showCancelConfirmationPopup, t, market }) {
  const [expandOrderInfo, setExpandOrderInfo] = useState(true);

  const handleExpandOrderInfo = useCallback(() => {
    setExpandOrderInfo(prev => !prev);
  }, []);

  const canCancel = useCallback((status) => {
    const cancellableStatuses = [
      "CREATED",
      "AMENDED",
      "ORDER_CONTRACTED",
      "ORDER_RESERVED",
      "DEALER_REJECTED",
      "ORDERED",
    ];
    return cancellableStatuses.includes(status);
  }, []);

  const order = customerReservation;
  const date_string = "DATE";

  return (
    <div>
      <StyledBasicExpansionPanel
        defaultExpanded={true}
        onChange={handleExpandOrderInfo}
      >
        <StyledBasicExpansionPanelSummary
          style={crStyles.headerBarNeutral}
          id="exp"
        >
          <div className={sharedStyles.headerBar}>
            <div className={sharedStyles.boldHeader}>
              {t("OrderInfo.header")}
            </div>

            <div className={styles.cancelDiv}>
              <HasPermissionTo
                perform={["cancelOrder:write"]}
                permissions={permissions}
                condition={canCancel(order.status)}
                render={() => (
                  <button
                    className={styles.cancelOrderButton}
                    id="cancelOrderBtn"
                    data-testid="cancelOrderBtn"
                    onClick={(e) => showCancelConfirmationPopup(e)}
                  >
                    {t("CustomerOrderDetails.cancelBtnTxt")}
                  </button>
                )}
              />

              {expandOrderInfo ? (
                <ExpandLessIcon id="less" />
              ) : (
                <ExpandMoreIcon id="more" />
              )}
            </div>
          </div>
          <br />
        </StyledBasicExpansionPanelSummary>
        <div className={styles.customerInfoGrid}>
          <div className={styles.bottomBorder} />

          {order ? (
            <Grid container className={styles.customerContainer}>
              <Grid item xs={3}>
                <div className={styles.infoHeader}>
                  {t("OrderInfo.orderDetailsSubheader")}
                </div>
                <InfoDetail
                  header={t("OrderInfo.orderNumber")}
                  value={order.id}
                />
                <InfoDetail
                  header={t("OrderDetails.reservationDate")}
                  value={order.reservationDate}
                />
                <InfoDetail
                  header={t("OrderInfo.orderStatus")}
                  value={order.businessStatus}
                />
                {order.cancellation &&
                  order.cancellation.cancellationDate && (
                    <InfoDetail
                      header={t("OrderInfo.cancellationDate")}
                      value={order.cancellation.cancellationDate}
                    />
                  )}
                {UpperCaseText(order.status) === "CONVERTED_TO_OFFLINE" ? (
                  <InfoDetail
                    header={`${t(
                      "CustomerReservations.deliveryScheduled"
                    )} ${date_string}`}
                    value={order.modifiedDate}
                  />
                ) : null}
                <InfoDetail
                  header={t("OrderDetails.channel")}
                  value={
                    UpperCaseText(order.orderType) === "RESERVATION"
                      ? order.reservationChannel
                      : order.orderChannel
                  }
                />
                {order.orderAmendedDate && (
                  <InfoDetail
                    header={t("OrderDetails.amendedDate")}
                    value={order.orderAmendedDate}
                  />
                )}
              </Grid>
              <Grid item xs={3}>
                <div className={styles.infoHeader}>&nbsp;</div>
                <InfoDetail
                  header={t("OrderInfo.vistaDate")}
                  value={order.orbitIdCreatedDate}
                />
                <InfoDetail
                  header={t("OrderInfo.vistaNumber")}
                  value={order.wbdoOrVistaOrderId}
                />
                <InfoDetail
                  header={t("OrderInfo.orbitNumber")}
                  value={order.orbitId}
                />
              </Grid>

              <Grid item xs={3}>
                <div className={styles.infoHeader}>
                  {t("OrderInfo.vehicleDetailsSubheader")}
                </div>
                <InfoDetail
                  header={t("OrderInfo.model")}
                  value={order.nameplate}
                />
                <InfoDetail
                  header={t("OrderInfo.series")}
                  value={
                    order.vehicleFeaturesForIMG &&
                    order.vehicleFeaturesForIMG.MarketedSeries
                  }
                />
                {market === "NZL" && (
                  <InfoDetail
                    header={
                      BEV_VEHICLES.includes(UpperCaseText(order.nameplate))
                        ? t("OrderInfo.battery")
                        : t("OrderInfo.engine")
                    }
                    value={
                      order.vehicleFeaturesForIMG &&
                      order.vehicleFeaturesForIMG.PowerTrain
                    }
                  />
                )}
                <InfoDetail
                  header={t("OrderInfo.bodyStyle")}
                  value={
                    order.vehicleFeaturesForIMG &&
                    order.vehicleFeaturesForIMG.BodyStyle
                  }
                />
                <InfoDetail
                  header={t("OrderInfo.colour")}
                  value={
                    order.vehicleFeaturesForIMG &&
                    order.vehicleFeaturesForIMG.PaintCode
                  }
                />
              </Grid>

              <Grid item xs={3}>
                <div className={styles.infoHeader}>&nbsp;</div>
                <InfoDetail
                  header={t("OrderInfo.interior")}
                  value={
                    order.vehicleFeaturesForIMG &&
                    order.vehicleFeaturesForIMG.Interior
                  }
                />
                <InfoDetail
                  header={t("OrderInfo.vin")}
                  value={order.vinNumber}
                />
                {TRANSIT_VEHICLES === UpperCaseText(order.nameplate) && (
                  <>
                    <InfoDetail
                      header={t("OrderInfo.capacity")}
                      value={
                        order.vehicleFeaturesForIMG &&
                        order.vehicleFeaturesForIMG.AF8
                      }
                    />
                    <InfoDetail
                      header={t("OrderInfo.gvm")}
                      value={
                        order.vehicleFeaturesForIMG &&
                        order.vehicleFeaturesForIMG.AF9
                      }
                    />
                  </>
                )}
              </Grid>
            </Grid>
          ) : (
            <span className={styles.labelError}>
              {" "}
              {t("CustomerOrderDetails.errorLoadingCustomerInfo")}
            </span>
          )}
        </div>
      </StyledBasicExpansionPanel>
    </div>
  );
}

export default withTranslation("emp")(OrderInfo);
